import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, ClipImage } from "../components/Reveal";
import { OrangeCta } from "../components/OrangeCta";
import { VERTICALS } from "../data/verticals";
import { SERVICES } from "../data/services";
import { PROJECTS } from "../data/work";
import { IMG, imgProps } from "../data/assets";
import NotFound from "./NotFound";

// Permanent industry vertical pages — unique content, services, challenges,
// project evidence, FAQs and CTA per vertical.
export default function IndustryPage() {
  const { slug } = useParams();
  const v = VERTICALS.find((x) => x.slug === slug);
  if (!v) return <NotFound />;

  const services = v.services.map((id) => SERVICES.find((s) => s.id === id)).filter(Boolean);
  const projects = v.evidence.map((id) => PROJECTS.find((p) => p.id === id)).filter(Boolean);

  const jsonLd = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Industries", path: "/industries" },
      { name: v.name, path: `/industries/${v.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: v.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <>
      <SEO title={v.seo.title} description={v.seo.description} path={`/industries/${v.slug}`} jsonLd={jsonLd} image={IMG[v.image].src} />
      <PageHero eyebrow="Industry" lines={[v.name + "."]} copy={v.hero} />

      <section className="bg-offwhite py-20 md:py-28">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="max-w-xl text-base leading-relaxed text-zinc-700 md:text-lg">{v.intro}</p>
            </Reveal>
            <div className="mt-10 space-y-0">
              {v.challenges.map((c, i) => (
                <Reveal key={c.h} delay={i * 0.05}>
                  <div className="border-b border-zinc-200 py-6">
                    <h2 className="font-display text-xl font-semibold uppercase tracking-tight text-charcoal">
                      <span className="mr-3 font-mono text-xs text-dcc-strong">{String(i + 1).padStart(2, "0")}</span>{c.h}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-600">{c.p}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <ClipImage src={IMG[v.image].src} alt={IMG[v.image].alt} ratio="aspect-[4/3]" className="border border-zinc-200" />
            <div className="mt-8 border border-zinc-200 bg-white p-7">
              <p className="eyebrow">Relevant DCC services</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {services.map((s) => (
                  <Link key={s.id} to={`/services/${s.slug}`} data-testid={`industry-service-${s.id}`}
                    className="border border-zinc-300 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-700 transition-colors hover:border-dcc hover:text-dcc-strong">
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {projects.length > 0 && (
        <section className="noise-dark relative overflow-hidden py-20 text-zinc-100 md:py-28" aria-label="Project evidence">
          <div className="absolute inset-0 grid-lines-dark" aria-hidden="true" />
          <div className="container-x relative">
            <Reveal><p className="eyebrow-dark">Project evidence</p></Reveal>
            <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2">
              {projects.map((p) => (
                <Link key={p.id} to={`/our-work/${p.id}`} data-testid={`industry-project-${p.id}`}
                  className="group flex h-full flex-col bg-charcoal transition-colors duration-300 hover:bg-zinc-950">
                  <div className="overflow-hidden">
                    <img src={IMG[p.image].src} alt={IMG[p.image].alt} {...imgProps(IMG[p.image].src)} loading="lazy" decoding="async"
                      className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{p.industry} · {p.region}</p>
                    <p className="mt-3 font-display text-lg font-semibold uppercase leading-tight tracking-tight text-zinc-50">{p.title}</p>
                    <p className="link-tech mt-4 text-zinc-300">Read the case study <ArrowUpRight size={13} aria-hidden="true" className="text-dcc-strong" /></p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-paper py-20 md:py-28">
        <div className="container-x max-w-4xl">
          <Reveal><h2 className="h-display text-3xl text-charcoal sm:text-4xl">Asked by {v.name.toLowerCase()} teams.</h2></Reveal>
          <Accordion type="single" collapsible className="mt-8 border-t border-zinc-200">
            {v.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-zinc-200">
                <AccordionTrigger data-testid={`industry-faq-${i}-trigger`} className="py-6 text-left font-display text-lg font-medium uppercase tracking-tight text-charcoal hover:text-dcc hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-sm leading-relaxed text-zinc-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <OrangeCta
        lines={["SHOW US THE FACILITY.", "WE'LL SHOW YOU THE PLAN."]}
        copy={`A free technical survey scopes what your ${v.name.toLowerCase()} facility actually needs — photographed, graded and documented before you commit to anything.`}
      />
    </>
  );
}
